import { useCallback } from "react";

import { BookedGuestSurveyDisplayStatus } from "@/lib/gql/__generated__/graphql";

import { useAccommodationHotelPolicyFormModelContext } from "@/components/features/items/edit/hotel-policy/default/ui/context";

import type { FormOptionType } from "@/components/ui/form/_types/type";

type PostBookingSurveyDisplaySelectValue = BookedGuestSurveyDisplayStatus;

const postBookingSurveyDisplayOptions = [
	{
		id: "postBookingSurveyDisplay_unset",
		label: "未設定",
		value: BookedGuestSurveyDisplayStatus.Unset,
	},
	{
		id: "postBookingSurveyDisplay_on",
		label: "オン",
		value: BookedGuestSurveyDisplayStatus.Visible,
	},
	{
		id: "postBookingSurveyDisplay_off",
		label: "オフ",
		value: BookedGuestSurveyDisplayStatus.Hidden,
	},
] satisfies FormOptionType[];

const POST_BOOKING_SURVEY_DISPLAY_VALUES = Object.values(BookedGuestSurveyDisplayStatus) as string[];

const isPostBookingSurveySelectValue = (input: string): input is PostBookingSurveyDisplaySelectValue => {
	return POST_BOOKING_SURVEY_DISPLAY_VALUES.includes(input);
};

/** InputModel が欠けるとき ViewModel ノードは欠落する。未設定として扱う。 */
const UNSET_POST_BOOKING_SURVEY_VIEW_MODEL_NODE: {
	value: BookedGuestSurveyDisplayStatus.Unset;
	isEdited: boolean;
	error: undefined;
} = {
	value: BookedGuestSurveyDisplayStatus.Unset,
	isEdited: false,
	error: undefined,
};

export const usePostBookingSurveyToggle = () => {
	const { viewModel, onSetViewModel } = useAccommodationHotelPolicyFormModelContext();
	const bookedGuestSurveyDisplayStatus =
		viewModel.bookedGuestSurveyDisplayStatus ?? UNSET_POST_BOOKING_SURVEY_VIEW_MODEL_NODE;

	const isEdited = bookedGuestSurveyDisplayStatus.isEdited;
	const hasError = Boolean(bookedGuestSurveyDisplayStatus.error);
	const isUnset = bookedGuestSurveyDisplayStatus.value === BookedGuestSurveyDisplayStatus.Unset;

	const onSetBookedGuestSurveyDisplayStatus = useCallback(
		(input: string) => {
			if (!isPostBookingSurveySelectValue(input)) {
				return;
			}

			onSetViewModel([
				{
					key: "bookedGuestSurveyDisplayStatus",
					value: {
						...bookedGuestSurveyDisplayStatus,
						isEdited: true,
						value: input,
						error: undefined,
					},
				},
			]);
		},
		[bookedGuestSurveyDisplayStatus, onSetViewModel],
	);

	return {
		isEdited,
		hasError,
		isUnset,
		bookedGuestSurveyDisplayStatus,
		postBookingSurveyDisplayOptions,
		onSetBookedGuestSurveyDisplayStatus,
	};
};
